import { useEffect, useCallback, useRef } from 'react';
import {
  DATA_REFRESH_EVENTS,
  subscribeToDataRefresh,
  subscribeToMultipleEvents,
} from '../utils/dataRefreshEvents';

/**
 * Hook zum Abonnieren von Daten-Refresh-Events
 *
 * @param {string|string[]} eventTypes - Event-Typ(en) zum Abonnieren
 * @param {function} onRefresh - Callback wenn Event eintritt (erhält event.detail)
 * @param {object} options - Optionen
 * @param {boolean} options.enabled - Ob der Hook aktiv ist (default: true)
 * @param {number} options.debounceMs - Debounce in ms (default: 100)
 */
export function useDataRefresh(eventTypes, onRefresh, options = {}) {
  const { enabled = true, debounceMs = 100 } = options;
  const timeoutRef = useRef(null);
  const callbackRef = useRef(onRefresh);

  // Callback-Ref aktuell halten
  useEffect(() => {
    callbackRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;

    const handleEvent = (event) => {
      // Debounce um mehrere schnelle Updates zusammenzufassen
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(event.detail);
      }, debounceMs);
    };

    // Single event oder multiple events?
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    const unsubscribe = subscribeToMultipleEvents(types, handleEvent);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      unsubscribe();
    };
  }, [eventTypes, enabled, debounceMs]);
}

/**
 * Hook speziell für Kunden-bezogene Updates
 * Reagiert auf Kunde erstellt/aktualisiert/gelöscht
 */
export function useKundenRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.KUNDE_CREATED,
    DATA_REFRESH_EVENTS.KUNDE_UPDATED,
    DATA_REFRESH_EVENTS.KUNDE_DELETED,
  ];

  const handleRefresh = useCallback((detail) => {
    // Wenn kundeId angegeben, nur bei diesem Kunden refreshen
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook speziell für Anfragen-bezogene Updates
 */
export function useAnfragenRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.ANFRAGE_CREATED,
    DATA_REFRESH_EVENTS.ANFRAGE_UPDATED,
    DATA_REFRESH_EVENTS.ANFRAGE_DELETED,
    DATA_REFRESH_EVENTS.ANFRAGE_STATUS_CHANGED,
  ];

  const handleRefresh = useCallback((detail) => {
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook speziell für Termin-bezogene Updates
 */
export function useTermineRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.TERMIN_CREATED,
    DATA_REFRESH_EVENTS.TERMIN_UPDATED,
    DATA_REFRESH_EVENTS.TERMIN_DELETED,
  ];

  const handleRefresh = useCallback((detail) => {
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook speziell für Rechnungs-bezogene Updates
 */
export function useRechnungenRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.RECHNUNG_CREATED,
    DATA_REFRESH_EVENTS.RECHNUNG_UPDATED,
    DATA_REFRESH_EVENTS.RECHNUNG_STATUS_CHANGED,
  ];

  const handleRefresh = useCallback((detail) => {
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook speziell für KVA-bezogene Updates
 */
export function useKvaRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.KVA_CREATED,
    DATA_REFRESH_EVENTS.KVA_UPDATED,
  ];

  const handleRefresh = useCallback((detail) => {
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook speziell für Foto-bezogene Updates
 */
export function useFotosRefresh(onRefresh, kundeId = null) {
  const events = [
    DATA_REFRESH_EVENTS.FOTO_SAVED,
  ];

  const handleRefresh = useCallback((detail) => {
    if (kundeId && detail.kundeId && detail.kundeId !== kundeId) {
      return;
    }
    onRefresh(detail);
  }, [onRefresh, kundeId]);

  useDataRefresh(events, handleRefresh);
}

/**
 * Hook für allgemeine Daten-Refresh Events
 * Nützlich für Dashboard oder Übersichtsseiten
 */
export function useGlobalDataRefresh(onRefresh) {
  useDataRefresh(DATA_REFRESH_EVENTS.REFRESH_ALL, onRefresh);
}

export { DATA_REFRESH_EVENTS };
